
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { X, CreditCard, Save, Lock, AlertTriangle } from 'lucide-react';

interface PaymentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PaymentConfigModal: React.FC<PaymentConfigModalProps> = ({ isOpen, onClose }) => {
  const { config, updateConfig, syncWithServer } = useApp();
  
  const [payuPosId, setPayuPosId] = useState(config.payuPosId || '');
  const [payuClientSecret, setPayuClientSecret] = useState(config.payuClientSecret || '');
  const [payuMd5Key, setPayuMd5Key] = useState(config.payuMd5Key || '');
  const [payuEnv, setPayuEnv] = useState<'sandbox' | 'secure'>(config.payuEnv || 'sandbox');
  
  const [isSaving, setIsSaving] = useState(false);

  // Load from context when open
  React.useEffect(() => {
      if(isOpen) {
          setPayuPosId(config.payuPosId || '');
          setPayuClientSecret(config.payuClientSecret || '');
          setPayuMd5Key(config.payuMd5Key || '');
          setPayuEnv(config.payuEnv || 'sandbox');
      }
  }, [isOpen, config]);

  const handleSave = async () => {
      setIsSaving(true);
      
      // 1. Update local state
      updateConfig({
          payuPosId,
          payuClientSecret,
          payuMd5Key,
          payuEnv
      });
      
      // 2. Wait a bit for state to settle, then sync with server
      // Note: In real React, we should probably pass the new config directly to sync, but our architecture uses AppState.
      // We will assume that updateConfig updates the state, and then we sync.
      
      // Use setTimeout to allow state propagation or invoke save manually
      // Here we rely on the user manually clicking "Save / Export" in ProjectManager usually,
      // but for keys we want immediate save to secure them.
      
      setTimeout(async () => {
          await syncWithServer();
          setIsSaving(false);
          alert('Konfiguracja PayU zapisana. Płatności powinny działać.');
          onClose();
      }, 500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
       <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-4 border-b bg-gray-900 text-white flex justify-between items-center">
             <h3 className="font-bold flex items-center gap-2">
               <CreditCard size={20} className="text-green-400"/> Konfiguracja PayU
             </h3>
             <button onClick={onClose}><X size={20} className="text-gray-400 hover:text-white"/></button>
          </div>
          
          <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-xs text-yellow-800 flex gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0"/>
                  <p>Dane te są poufne. Są używane przez serwer do autoryzacji płatności. Upewnij się, że strona działa na HTTPS.</p>
              </div>

              <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Środowisko (Environment)</label>
                  <div className="flex bg-gray-100 p-1 rounded">
                      <button 
                        onClick={() => setPayuEnv('sandbox')}
                        className={`flex-1 py-1 text-xs font-bold rounded ${payuEnv === 'sandbox' ? 'bg-white shadow text-black' : 'text-gray-500'}`}
                      >
                          Sandbox (Testy)
                      </button>
                      <button 
                        onClick={() => setPayuEnv('secure')}
                        className={`flex-1 py-1 text-xs font-bold rounded ${payuEnv === 'secure' ? 'bg-green-600 shadow text-white' : 'text-gray-500'}`}
                      >
                          Produkcja (Secure)
                      </button>
                  </div>
              </div>

              <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">POS ID (id punktu płatności)</label>
                  <input 
                    value={payuPosId}
                    onChange={(e) => setPayuPosId(e.target.value)}
                    className="w-full border border-gray-300 rounded p-2 text-sm font-mono"
                    placeholder="np. 300746"
                  />
              </div>

              <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Client Secret (Sekret Klienta)</label>
                  <div className="relative">
                      <Lock size={14} className="absolute left-3 top-2.5 text-gray-400"/>
                      <input 
                        type="password"
                        value={payuClientSecret}
                        onChange={(e) => setPayuClientSecret(e.target.value)}
                        className="w-full border border-gray-300 rounded pl-9 pr-2 py-2 text-sm font-mono"
                        placeholder="Klucz OAuth..."
                      />
                  </div>
              </div>

              <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">MD5 Key (Do weryfikacji powiadomień)</label>
                  <div className="relative">
                      <Lock size={14} className="absolute left-3 top-2.5 text-gray-400"/>
                      <input 
                        type="password"
                        value={payuMd5Key}
                        onChange={(e) => setPayuMd5Key(e.target.value)}
                        className="w-full border border-gray-300 rounded pl-9 pr-2 py-2 text-sm font-mono"
                        placeholder="Klucz MD5..."
                      />
                  </div>
              </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
             <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded text-sm font-bold">Anuluj</button>
             <button 
                onClick={handleSave} 
                disabled={isSaving}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 rounded text-sm font-bold flex items-center gap-2 disabled:opacity-50"
             >
                {isSaving ? 'Zapisywanie...' : <><Save size={16}/> Zapisz Klucze</>}
             </button>
          </div>
       </div>
    </div>
  );
};

export default PaymentConfigModal;
